//External Libraries
import React, { useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

//Components
import { CMDivider } from "@chartmetric/chartmetric-design-system/components/CMDivider";
import { CMFlex } from "@chartmetric/chartmetric-design-system/components/CMFlex";
import { CMText } from "@chartmetric/chartmetric-design-system/components/CMText";
import AnnouncementCard from "./AnnouncementCard";

//Types
import type { Announcement } from "../types";
import CMIcon from "@chartmetric/chartmetric-design-system/components/CMIcon";

interface AnnouncementListProps {
  announcements: Announcement[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onReactionChange?: () => void;
}

function AnnouncementList({
  announcements,
  loading,
  hasMore,
  onLoadMore,
  onReactionChange,
}: AnnouncementListProps) {
  const { t } = useTranslation("common", { keyPrefix: "announcementPanel" });
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || loading) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) onLoadMore();
      },
      { threshold: 0 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [onLoadMore, loading, announcements.length]);

  if (loading && announcements.length === 0) {
    return (
      <CMFlex
        align="center"
        justify="center"
        p="xl"
        fullWidth
        height="h-[80vh]"
      >
        <CMIcon icon="spinner" size="2xl" loading />
      </CMFlex>
    );
  }

  if (announcements.length === 0) {
    return (
      <CMFlex align="center" justify="center" p="2xl">
        <CMText value={t("noAnnouncements")} size="sm" color="tertiary" />
      </CMFlex>
    );
  }

  return (
    <CMFlex vertical flex fullHeight>
      {announcements.map((announcement, index) => (
        <React.Fragment key={announcement.id}>
          {index > 0 && <CMDivider my="none" color="lightGray" />}
          <AnnouncementCard
            announcement={announcement}
            onReactionChange={onReactionChange}
          />
        </React.Fragment>
      ))}
      {hasMore && (
        <CMFlex ref={sentinelRef} justify="center" p="lg">
          <CMIcon icon="spinner" size="2xl" loading />
        </CMFlex>
      )}
    </CMFlex>
  );
}

export default AnnouncementList;
